/**
 * useBetaActive — Detecta si la beta Founder sigue activa (Sprint 10.25).
 *
 * Beta activa = hay cupos Founder disponibles (Pro o Premium).
 * Cuando ambos cupos llegan a 0, la beta cerró y el plan Free se reactiva.
 *
 * Usado por:
 * - PublicLayout: texto del botón CTA del header
 * - LandingPage: texto del CTA final
 *
 * Reutiliza el hook useFounderSeats() — TanStack Query dedupe la query
 * por queryKey, así que no hay overhead extra.
 */
import { useFounderSeats } from '@/lib/api/subscription';
import { supabase } from '@/lib/supabase';

export interface BetaStatus {
  isBetaActive: boolean;
  /** Total de cupos founder disponibles. 0 = beta cerrada. */
  availableSeats: number;
  /** Mientras carga, asumimos que SÍ está en beta (más seguro para copy). */
  isLoading: boolean;
}

export function useBetaActive(): BetaStatus {
  const { data: seats, isLoading } = useFounderSeats();
  const availableSeats =
    (seats?.pro_founder?.available_seats ?? 0) +
    (seats?.premium_founder?.available_seats ?? 0);
  // Hasta saber, asumimos beta activa: copy más conservador con cupos limitados
  // en lugar de prometer Free que aún no existe.
  const isBetaActive = isLoading ? true : availableSeats > 0;
  return { isBetaActive, availableSeats, isLoading };
}

/**
 * ¿El plan Free está abierto? (freemium, 29-sep-2026)
 *
 * Free abierto = beta cerrada: el botón "Cerrar beta" de /admin/founders
 * apaga los Founder y prende el Free. Mientras carga devolvemos false (el
 * comportamiento de beta, el más conservador).
 */
export function useFreePlanOpen(): { isFreeOpen: boolean; isLoading: boolean } {
  const { isBetaActive, isLoading } = useBetaActive();
  return { isFreeOpen: !isLoading && !isBetaActive, isLoading };
}

/**
 * Versión imperativa de useFreePlanOpen, para flujos async fuera del render
 * (ej. el callback de Google). Si falla la consulta, asume beta (false).
 */
export async function fetchFreePlanOpen(): Promise<boolean> {
  try {
    const codes = ['pro_founder', 'premium_founder'] as const;
    const results = await Promise.all(
      codes.map((c) => supabase.rpc('available_founder_seats', { _plan_code: c })),
    );
    if (results.some((r) => r.error)) return false;
    const available = results.reduce(
      (sum, r) => sum + ((r.data as Array<{ available_seats: number }> | null)?.[0]?.available_seats ?? 0),
      0,
    );
    return available <= 0;
  } catch {
    return false;
  }
}

/** Copy del CTA principal según estado de beta + destino correcto.
 *
 * Durante beta (sin Free activo): cualquier CTA público debe llevar a
 * /pricing — el flujo es elegir plan → checkout → registro durante
 * checkout. Mandar a /register sin más deja al user con cuenta vacía
 * sin posibilidad de hacer nada.
 *
 * Post-beta (Free activo): tiene sentido /register directo — entran
 * gratis y pueden upgrade después.
 */
export function ctaCopy(isBetaActive: boolean): {
  header: string;
  final: string;
  /** Destino de cualquier CTA público de captura. */
  href: string;
} {
  // Replanteamiento 2026: "Únete al club" en vez de "Empieza hoy".
  // "Entrar" (login) y "Únete" (captura) no compiten semánticamente, y el
  // botón más visto del sitio repite la categoría de marca (el club).
  // Freemium (beta cerrada): la acción es crear la cuenta gratis, dicho así.
  return {
    header: isBetaActive ? 'Únete al club' : 'Empieza gratis',
    final: isBetaActive ? 'Únete al club' : 'Empieza gratis',
    href: isBetaActive ? '/pricing' : '/register',
  };
}
